import React from 'react';
import { motion } from 'framer-motion';
import { SectionHeader } from '../components/SectionHeader';
import { Button } from '../components/Button';
import { FAQAccordion } from '../components/FAQAccordion';
import { GlowOrb } from '../components/GlowOrb';
import { CheckCircle2, Gift, ShieldCheck } from 'lucide-react';
import { faqs } from '../data/content';
export function Pricing() {
  const packages = [{
    name: 'Essential Alignment',
    price: '79',
    description: 'Perfect for a quick energetic reset and grounding.',
    features: ['One Chakra Balancing Session', 'Initial Energy Scan', 'Personalized Guidance Notes', 'Email Support for 3 Days'],
    popular: false
  }, {
    name: 'Deep Healing Journey',
    price: '149',
    description: 'Our most comprehensive clearing and balancing session.',
    features: ['Full Chakra Balancing', 'Complete Aura Cleansing', 'Follow-Up Guidance Call', 'Custom Meditation Recording', 'Priority Email Support'],
    popular: true
  }, {
    name: 'Cosmic Clarity',
    price: '199',
    description: 'Deep dive into your life path and cosmic blueprint.',
    features: ['Full Astrology Reading (90m)', 'Life Path & Career Analysis', '6-Month Future Transit Guide', 'Relationship Compatibility Check', 'Recording of Session'],
    popular: false
  }, {
    name: 'Premium Membership',
    price: '299',
    period: '/mo',
    description: 'Ongoing support for dedicated spiritual seekers.',
    features: ['Unlimited Healing Access', 'Weekly Group Sessions', 'Private Community Access', 'Exclusive Healing Library', 'Direct WhatsApp Access'],
    popular: false
  }];
  return <div className="pt-32 pb-24 relative overflow-hidden" data-id="element-786">
      <div className="absolute top-0 left-0 right-0 h-[80vh] bg-cover bg-center opacity-20" style={{
      backgroundImage: `url(https://images.unsplash.com/photo-1518709268805-4e9042af2176?q=80&w=2000&auto=format&fit=crop)`
    }} data-id="element-787"></div>
      <div className="absolute top-0 left-0 right-0 h-[80vh] bg-gradient-to-b from-midnight/40 via-midnight/80 to-midnight" data-id="element-788"></div>
      <GlowOrb color="gold" size="xl" position="top-0 left-1/2 -translate-x-1/2" data-id="element-789" />

      <section className="container mx-auto px-6 mb-32 relative z-10" data-id="element-790">
        <SectionHeader eyebrow="Invest In Yourself" title="Healing Packages" description="Choose the healing journey that aligns with your current energetic needs. All sessions are conducted securely online." data-id="element-791" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-[1400px] mx-auto mt-20" data-id="element-792">
          {packages.map((pkg, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className={`relative rounded-[2.5rem] p-8 flex flex-col ${pkg.popular ? 'bg-gradient-to-b from-primary-light to-primary border-gold/50 shadow-[0_0_40px_rgba(212,175,110,0.2)] lg:-translate-y-4 z-10' : 'glass hover:border-gold/30'}`} data-id="element-793">
              {pkg.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gold text-midnight text-[10px] font-bold uppercase tracking-widest py-1.5 px-6 rounded-full shadow-lg" data-id="element-794">
                  Most Popular
                </div>}

              <h3 className={`text-2xl font-serif mb-3 ${pkg.popular ? 'text-white' : 'text-white'}`} data-id="element-795">
                {pkg.name}
              </h3>
              <p className="text-sm mb-8 text-beige-muted h-10" data-id="element-796">
                {pkg.description}
              </p>

              <div className="mb-10" data-id="element-797">
                <span className="text-5xl font-serif font-bold text-white" data-id="element-798">
                  ${pkg.price}
                </span>
                <span className="text-sm text-beige-muted ml-1" data-id="element-799">
                  {pkg.period || '/session'}
                </span>
              </div>

              <ul className="space-y-5 mb-10 flex-grow" data-id="element-800">
                {pkg.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-801">
                    <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${pkg.popular ? 'text-gold' : 'text-gold/70'}`} data-id="element-802" />
                    <span className="text-beige text-sm leading-tight" data-id="element-803">
                      {feature}
                    </span>
                  </li>)}
              </ul>

              <Button href="/booking" fullWidth variant={pkg.popular ? 'primary' : 'glass'} className="mt-auto" data-id="element-804">
                Book Now
              </Button>
            </motion.div>)}
        </div>
      </section>

      {/* Guarantee Badge */}
      <section className="container mx-auto px-6 mb-32" data-id="element-805">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="max-w-3xl mx-auto glass p-8 rounded-3xl flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left border-gold/20" data-id="element-806">
          <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0" data-id="element-807">
            <ShieldCheck className="w-8 h-8 text-gold" data-id="element-808" />
          </div>
          <div data-id="element-809">
            <h3 className="text-xl font-serif text-white mb-2" data-id="element-810">
              100% Satisfaction Guarantee
            </h3>
            <p className="text-beige-muted text-sm" data-id="element-811">
              If you don't feel a positive shift after your first session, we
              will refund your investment in full. Your healing is our priority.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Gift Card Section - Inverted Theme for rhythm */}
      <section className="bg-[#F2E8D5] py-32 relative overflow-hidden" data-id="element-812">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-multiply" data-id="element-813"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-814">
          <div className="max-w-5xl mx-auto bg-white rounded-[3rem] p-10 md:p-16 shadow-2xl flex flex-col md:flex-row items-center gap-16" data-id="element-815">
            <div className="flex-1" data-id="element-816">
              <div className="w-20 h-20 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-8" data-id="element-817">
                <Gift className="w-10 h-10" data-id="element-818" />
              </div>
              <h2 className="text-4xl font-serif text-midnight mb-6" data-id="element-819">
                Give the Gift of Healing
              </h2>
              <p className="text-midnight/70 mb-8 text-lg leading-relaxed" data-id="element-820">
                A Calmoras gift card is a beautiful way to support a loved one's
                spiritual journey. Perfect for birthdays, life transitions, or
                just because.
              </p>
              <ul className="space-y-4 text-midnight/80 font-medium" data-id="element-821">
                <li className="flex items-center gap-3" data-id="element-822">
                  <span className="w-2 h-2 rounded-full bg-gold" data-id="element-823"></span>{' '}
                  Delivered instantly via email
                </li>
                <li className="flex items-center gap-3" data-id="element-824">
                  <span className="w-2 h-2 rounded-full bg-gold" data-id="element-825"></span> Never
                  expires
                </li>
                <li className="flex items-center gap-3" data-id="element-826">
                  <span className="w-2 h-2 rounded-full bg-gold" data-id="element-827"></span> Can be
                  used for any service
                </li>
              </ul>
            </div>

            <div className="flex-1 w-full bg-midnight p-10 rounded-[2rem] shadow-xl relative overflow-hidden" data-id="element-828">
              <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
              backgroundImage: `url(https://images.unsplash.com/photo-1518791841217-8f162f1e1131?q=80&w=1200&auto=format&fit=crop)`
            }} data-id="element-829"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-midnight/95 via-midnight/85 to-primary/40" data-id="element-830"></div>
              <form className="space-y-6 relative z-10" onSubmit={e => e.preventDefault()} data-id="element-831">
                <div data-id="element-832">
                  <label className="text-sm font-medium text-beige mb-3 block uppercase tracking-wider" data-id="element-833">
                    Select Amount
                  </label>
                  <div className="grid grid-cols-3 gap-3" data-id="element-834">
                    {['$79', '$149', '$199'].map(amt => <button key={amt} type="button" className="py-3 glass rounded-xl hover:border-gold hover:text-gold transition-colors text-white font-serif text-xl" data-id="element-835">
                        {amt}
                      </button>)}
                  </div>
                </div>
                <div data-id="element-836">
                  <label className="text-sm font-medium text-beige mb-3 block uppercase tracking-wider" data-id="element-837">
                    Recipient Email
                  </label>
                  <input type="email" className="w-full px-5 py-4 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:border-gold text-white placeholder:text-white/30" placeholder="friend@email.com" data-id="element-838" />
                </div>
                <Button fullWidth variant="primary" size="lg" className="mt-4" data-id="element-839">
                  Send Gift Card
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-32" data-id="element-840">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-841">
          <SectionHeader eyebrow="Pricing & Policies" title="Frequently Asked Questions" data-id="element-842" />
          <FAQAccordion items={faqs} data-id="element-843" />
        </div>
      </section>
    </div>;
}